import { endOfDay, startOfDay, sub } from 'date-fns';
import { create, StoreApi, UseBoundStore } from 'zustand';

import { SortingState } from '@tanstack/react-table';

import { serializeSort } from './helpers';

export { serializeSort };

// NOTE: this store backs the Inbound Referrals inbox (InboundReferralsListPage) only.
// stores/referralsStore.ts is a separate, legacy Review-typed referrals page — not this one.

// In display order. Awaiting Admission (OLIO-3176) sits between the two: referrals whose Selected
// outcome queued a patient who has not yet been admitted — out of the working list, not yet a
// record. The api decides membership (awaiting_admission); Active and History subtract it.
export type Tab = 'active' | 'awaitingAdmission' | 'history';

// The chip row's vocabulary (Active tab only). 'actionNeeded' means the per-building queue
// (queue=true — work MY buildings can act on), not the workflow-derived list_status — see
// InboundReferralsListPage's chipParams. The Status dropdown that used to sit beside the chips
// (with its own 'actionNeeded' meaning list_status=action_needed) is gone (OLIO-3172): two
// params behind one label kept returning rows whose pills didn't say "Action Needed".
export type StatusChip = 'all' | 'actionNeeded' | 'readyForReview';

// The History tab's chip row (OLIO-3295): the recorded outcome, filtered server-side on
// `outcome`. 'all' is every concluded referral the other filters allow, archived included only
// when the checkbox says so.
export type HistoryChip = 'all' | 'selected' | 'notSelected' | 'denied';

// Where the Filters slideout keeps its selection, mirroring admissionsStore's `filters` shape. The
// Status dropdown that used to live here went away with OLIO-3172; this holds the Referral Source
// selection (OLIO-3161) and the History tab's "Show Archived Referrals" checkbox (OLIO-3295).
//
// The names in it are the client's own reporting names, not the wording a referral captured:
// several wordings resolve to one name, so filtering on the raw value would split one hospital
// across its spellings.
export type InboundReferralsFilterState = { referralSource?: string[]; showArchived?: boolean };

// Each tab has its own natural order: Active is newest-created first (the inbox), Awaiting
// Admission is most-recently-selected first (Time Since Selection, OLIO-3176), History is
// most-recently-concluded first (the mockup's Date Completed sort). Switching tabs resets to the
// tab's default — a Date Completed sort has no column to land on back on Active anyway.
export const ACTIVE_DEFAULT_SORT: SortingState = [{ id: 'createdAt', desc: true }];
export const AWAITING_DEFAULT_SORT: SortingState = [{ id: 'selectedAt', desc: true }];
export const HISTORY_DEFAULT_SORT: SortingState = [{ id: 'concludedAt', desc: true }];
// A record rather than a ternary so a fourth tab cannot silently inherit another tab's order.
const DEFAULT_SORTS: Record<Tab, SortingState> = {
  active: ACTIVE_DEFAULT_SORT,
  awaitingAdmission: AWAITING_DEFAULT_SORT,
  history: HISTORY_DEFAULT_SORT,
};
export const defaultSortFor = (tab: Tab): SortingState => DEFAULT_SORTS[tab];

// A History range is whole local days: the picker chooses calendar days in the viewer's zone, so
// the bounds sent are that zone's first and last instant of each day — the api honours instants
// as given (Workflow::QueryAttributes::DateRange) rather than re-bucketing them in UTC.
export const wholeDays = ([start, end]: [Date, Date]): [Date, Date] => [startOfDay(start), endOfDay(end)];

// The History date range's default — the last 30 days, like the outbound Active Referrals page
// (ReferralsFilterBar). A function, not a value: the store holds `null` until the person picks a
// range, and the page computes the default when History is shown, so a tab left open overnight
// does not keep yesterday's window.
export const defaultDateRange = (): [Date, Date] => {
  const now = new Date();
  return wholeDays([sub(now, { days: 30 }), now]);
};

type InboundReferralsActions = {
  setSort: (sort: SortingState) => void;
  setSearch: (search: string) => void;
  setTab: (tab: Tab) => void;
  setStatusChip: (chip: StatusChip) => void;
  setHistoryChip: (chip: HistoryChip) => void;
  setDateRange: (range: [Date, Date]) => void;
  setFilters: (
    filters: InboundReferralsFilterState | ((prev: InboundReferralsFilterState) => InboundReferralsFilterState)
  ) => void;
};

export type InboundReferralsState = {
  sort: SortingState;
  search: string;
  tab: Tab;
  statusChip: StatusChip;
  historyChip: HistoryChip;
  // The History tab's Date Completed range (OLIO-3295); null means "the default, computed when
  // shown" (defaultDateRange). The Active tab ignores it.
  dateRange: [Date, Date] | null;
  filters: InboundReferralsFilterState;
  actions: InboundReferralsActions;
};

const createInboundReferralsStore = (): [
  useStore: UseBoundStore<StoreApi<InboundReferralsState>>,
  useActions: () => InboundReferralsActions,
] => {
  const useStore = create<InboundReferralsState>((set) => ({
    sort: ACTIVE_DEFAULT_SORT,
    search: '',
    tab: 'active',
    statusChip: 'all',
    historyChip: 'all',
    dateRange: null,
    filters: {},
    actions: {
      setSort: (sort) => set({ sort }),
      setSearch: (search) => set({ search }),
      // Re-clicking the tab already showing changes nothing — a sort the person chose survives.
      setTab: (tab) => set((state) => (state.tab === tab ? {} : { tab, sort: defaultSortFor(tab) })),
      setStatusChip: (statusChip) => set({ statusChip }),
      setHistoryChip: (historyChip) => set({ historyChip }),
      setDateRange: (dateRange) => set({ dateRange: wholeDays(dateRange) }),
      setFilters: (filters) =>
        set((state) => ({
          filters: typeof filters === 'function' ? filters(state.filters) : filters,
        })),
    },
  }));

  const useActions = () => useStore((state) => state.actions);

  return [useStore, useActions];
};

export const [useInboundReferralsStore, useInboundReferralsActions] = createInboundReferralsStore();
